import React, { useState } from 'react';
import { 
  Building2, 
  FileText, 
  Calendar, 
  DollarSign, 
  Users, 
  Plus, 
  Search, 
  Filter,
  Edit,
  Eye,
  Download,
  Share2,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useNavigate } from 'react-router-dom';

interface LegalDocument {
  id: string;
  title: string;
  type: string;
  status: 'draft' | 'review' | 'signed' | 'expired';
  createdDate: string;
  lastModified: string;
  fileSize: string;
  attorney?: string;
}

interface Will {
  id: string;
  title: string;
  status: 'draft' | 'review' | 'signed' | 'executed';
  createdDate: string;
  beneficiaries: string[];
  executor: string;
  attorney: string;
}

interface PowerOfAttorney {
  id: string;
  title: string;
  type: 'financial' | 'healthcare' | 'general';
  agent: string;
  status: 'active' | 'inactive' | 'expired';
  effectiveDate: string;
  expiryDate?: string;
}

export function MobileLegalDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'documents' | 'wills' | 'poa' | 'insurance'>('documents');
  const [searchQuery, setSearchQuery] = useState('');

  // Mock data
  const documents: LegalDocument[] = [
    {
      id: '1',
      title: 'Last Will and Testament',
      type: 'Will',
      status: 'signed',
      createdDate: '2024-01-15',
      lastModified: '2024-01-20',
      fileSize: '2.4 MB',
      attorney: 'Smith & Associates'
    },
    {
      id: '2',
      title: 'Power of Attorney',
      type: 'POA',
      status: 'review',
      createdDate: '2024-01-10',
      lastModified: '2024-01-18',
      fileSize: '1.8 MB',
      attorney: 'Johnson Legal'
    },
    {
      id: '3',
      title: 'Living Trust',
      type: 'Trust',
      status: 'draft',
      createdDate: '2024-01-05',
      lastModified: '2024-01-12',
      fileSize: '3.2 MB',
      attorney: 'Davis Law Firm'
    }
  ];

  const wills: Will[] = [
    {
      id: '1',
      title: 'Primary Will',
      status: 'signed',
      createdDate: '2024-01-15',
      beneficiaries: ['Spouse', 'Children'],
      executor: 'John Smith',
      attorney: 'Smith & Associates'
    },
    {
      id: '2',
      title: 'Contingent Will',
      status: 'draft',
      createdDate: '2024-01-10',
      beneficiaries: ['Charity A', 'Charity B'],
      executor: 'Jane Doe',
      attorney: 'Johnson Legal'
    }
  ];

  const powerOfAttorneys: PowerOfAttorney[] = [
    {
      id: '1',
      title: 'Financial POA',
      type: 'financial',
      agent: 'Spouse',
      status: 'active',
      effectiveDate: '2024-01-15',
      expiryDate: '2029-01-15'
    },
    {
      id: '2',
      title: 'Healthcare POA',
      type: 'healthcare',
      agent: 'Adult Child',
      status: 'active',
      effectiveDate: '2024-01-10'
    }
  ];

  const insurance = [
    {
      id: '1',
      type: 'Life Insurance',
      provider: 'MetLife',
      policyNumber: 'POL123456',
      coverage: 500000,
      premium: 150,
      status: 'active'
    },
    {
      id: '2',
      type: 'Disability Insurance',
      provider: 'Guardian',
      policyNumber: 'POL789012',
      coverage: 100000,
      premium: 75,
      status: 'active'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'signed':
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'review':
        return 'text-yellow-600 bg-yellow-100';
      case 'draft':
        return 'text-blue-600 bg-blue-100';
      case 'expired':
      case 'inactive':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const renderDocuments = () => (
    <div className="space-y-3">
      {documents.map((document) => (
        <Card key={document.id} className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <FileText size={16} className="text-blue-500" />
                <h3 className="font-medium text-gray-900">{document.title}</h3>
              </div>
              <p className="text-sm text-gray-600">{document.type} • {document.attorney}</p>
              <p className="text-xs text-gray-500">{document.createdDate} • {document.fileSize}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(document.status)}`}>
                {document.status}
              </span>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost">
                  <Eye size={14} />
                </Button>
                <Button size="sm" variant="ghost">
                  <Download size={14} />
                </Button>
                <Button size="sm" variant="ghost">
                  <Edit size={14} />
                </Button>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  const renderWills = () => (
    <div className="space-y-3">
      {wills.map((will) => (
        <Card key={will.id} className="p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{will.title}</h3>
                <p className="text-sm text-gray-600">Attorney: {will.attorney}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(will.status)}`}>
                {will.status}
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-gray-600">Executor: {will.executor}</p>
              <p className="text-sm text-gray-600">Beneficiaries: {will.beneficiaries.join(', ')}</p>
              <p className="text-xs text-gray-500">Created: {will.createdDate}</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1">
                <Eye size={14} className="mr-1" />
                View
              </Button>
              <Button size="sm" variant="outline" className="flex-1">
                <Edit size={14} className="mr-1" />
                Edit
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  const renderPOA = () => (
    <div className="space-y-3">
      {powerOfAttorneys.map((poa) => (
        <Card key={poa.id} className="p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{poa.title}</h3>
                <p className="text-sm text-gray-600">{poa.type} Power of Attorney</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(poa.status)}`}>
                {poa.status}
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-gray-600">Agent: {poa.agent}</p>
              <p className="text-sm text-gray-600">Effective: {poa.effectiveDate}</p>
              {poa.expiryDate && (
                <p className="text-sm text-gray-600">Expires: {poa.expiryDate}</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1">
                <Eye size={14} className="mr-1" />
                View
              </Button>
              <Button size="sm" variant="outline" className="flex-1">
                <Edit size={14} className="mr-1" />
                Edit
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  const renderInsurance = () => (
    <div className="space-y-3">
      {insurance.map((policy) => (
        <Card key={policy.id} className="p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{policy.type}</h3>
                <p className="text-sm text-gray-600">{policy.provider}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(policy.status)}`}>
                {policy.status}
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-gray-600">Policy: {policy.policyNumber}</p>
              <p className="text-sm text-gray-600">Coverage: ${policy.coverage.toLocaleString()}</p>
              <p className="text-sm text-gray-600">Premium: ${policy.premium}/month</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1">
                <Eye size={14} className="mr-1" />
                View
              </Button>
              <Button size="sm" variant="outline" className="flex-1">
                <Edit size={14} className="mr-1" />
                Edit
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="p-4 pb-safe space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Legal Estate Planning</h2>
          <p className="text-sm text-gray-600">Manage wills, POAs, and legal documents</p>
        </div>
        <Button size="sm">
          <Plus size={16} className="mr-2" />
          Add Document
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search documents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
        />
      </div>

      {/* Tab Navigation */}
      <div className="grid grid-cols-2 gap-2">
        <Button
          variant={activeTab === 'documents' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('documents')}
        >
          <FileText size={16} className="mr-2" />
          Documents
        </Button>
        <Button
          variant={activeTab === 'wills' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('wills')}
        >
          <Building2 size={16} className="mr-2" />
          Wills
        </Button>
        <Button
          variant={activeTab === 'poa' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('poa')}
        >
          <Users size={16} className="mr-2" />
          POA
        </Button>
        <Button
          variant={activeTab === 'insurance' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('insurance')}
        >
          <DollarSign size={16} className="mr-2" />
          Insurance
        </Button>
      </div>

      {/* Content */}
      <div className="space-y-4">
        {activeTab === 'documents' && renderDocuments()}
        {activeTab === 'wills' && renderWills()}
        {activeTab === 'poa' && renderPOA()}
        {activeTab === 'insurance' && renderInsurance()}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <Button variant="outline" className="w-full" onClick={() => navigate('/dashboard/legal/wills', { state: { openWillBuilder: true } })}>
          <FileText size={16} className="mr-2" />
          Create Will
        </Button>
        <Button variant="outline" className="w-full">
          <Users size={16} className="mr-2" />
          Setup POA
        </Button>
      </div>

      {/* Alerts */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle size={16} className="text-red-600" />
          <h4 className="font-medium text-red-800">Legal Reminders</h4>
        </div>
        <div className="space-y-1 text-sm text-red-700">
          <p>• Will review due in 3 months</p>
          <p>• POA expires in 6 months</p>
        </div>
      </div>
    </div>
  );
} 